import { Link } from "react-router-dom";
import { UserCircle2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SectionContainer } from "@/components/marketing/SectionContainer";
import { CtaBand } from "@/components/marketing/CtaBand";
import { usePageMeta } from "./usePageMeta";

export function About() {
  usePageMeta({
    title: "About",
    description:
      "We're building the hiring platform for AI teammates. Small, senior team. Heads-down through private beta.",
  });
  return (
    <>
      <SectionContainer>
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">About</p>
          <h1 className="mt-3 font-display text-5xl md:text-6xl lg:text-7xl tracking-wide leading-[0.95] text-brand-ink">
            We're building the hiring platform for AI teammates.
          </h1>
        </div>

        <div className="mt-12 max-w-3xl space-y-6 text-lg text-brand-ink/75 leading-relaxed">
          <p>
            Hiring is slow, expensive, and almost never optimal for the work a team actually has in
            front of it. Contractors churn. Agencies are expensive. Offshore is a separate tooling
            problem. Meanwhile, every org has a backlog of work that would move if only it had
            more hands.
          </p>
          <p>
            VirtualDesk flips the script. You describe the role, set the boundaries, hand over the
            tools — and your virtual teammate is on the job in minutes. Not a chatbot. Not a
            copilot sidebar. An employee with a job description, a seat, and an audit trail.
          </p>
          <p>
            We built VirtualDesk on the conviction that the next decade of work is half human, half
            agentic, and the winners will be the teams that learn to staff both well.
          </p>
        </div>
      </SectionContainer>

      <SectionContainer className="border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">
            Why now
          </p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
            The tools finally caught up.
          </h2>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          <WhyNowCard
            title="Models are reliable enough"
            body="Claude 4 and Gemini 2 are consistent enough for production work when paired with clear boundaries, tool scopes, and approval gates."
          />
          <WhyNowCard
            title="Agent runtimes are boring"
            body="SDKs and platforms exist that turn an LLM into a process with memory, tools, and observability. We stand on that foundation."
          />
          <WhyNowCard
            title="Labor markets are tight"
            body="Engineering, ops, and customer teams face a hiring gap they can't close fast enough. A virtual teammate buys months of runway."
          />
        </div>
      </SectionContainer>

      <SectionContainer className="border-t border-white/5">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-violet">Team</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
            A small, senior team.
          </h2>
          <p className="mt-3 text-base text-brand-ink/70">
            We're staying heads-down through private beta. Founding team bios land here when we
            ship V1 publicly.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-white/10 bg-white/5 p-5 flex flex-col items-center text-center"
            >
              <div className="rounded-full bg-white/5 p-4">
                <UserCircle2 className="h-10 w-10 text-brand-ink/40" />
              </div>
              <p className="mt-4 text-sm font-semibold text-brand-ink">Coming soon</p>
              <p className="mt-1 text-xs text-brand-ink/50">Founder bio forthcoming</p>
            </div>
          ))}
        </div>
      </SectionContainer>

      <SectionContainer className="border-t border-white/5">
        <div className="max-w-xl">
          <h2 className="font-display text-4xl md:text-5xl tracking-wide text-brand-ink">
            Want to talk?
          </h2>
          <p className="mt-4 text-base text-brand-ink/70">
            We're always happy to hear from teams thinking about virtual staff, founders looking
            for pilot slots, or engineers interested in building here.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button asChild variant="primary">
              <Link to="/contact">Contact us</Link>
            </Button>
            <Button asChild variant="outline" className="border-white/20 text-brand-ink hover:bg-white/5">
              <Link to="/register">Start your workspace</Link>
            </Button>
          </div>
        </div>
      </SectionContainer>

      <CtaBand />
    </>
  );
}

function WhyNowCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-6">
      <h3 className="text-lg font-semibold text-brand-ink">{title}</h3>
      <p className="mt-3 text-sm text-brand-ink/70 leading-relaxed">{body}</p>
    </div>
  );
}
